/**
 * CodePreview — Real-time TC-DSL output panel.
 * Shows generated DSL text with syntax highlighting.
 */

import { useMemo, useCallback, useState } from 'react';
import clsx from 'clsx';
import { useStrategy } from '../../stores/builderStore';
import { generateDSL } from '../../services/dslService';

export function CodePreview() {
  const strategy = useStrategy();
  const [copied, setCopied] = useState(false);

  const dsl = useMemo(() => generateDSL(strategy), [strategy]);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(dsl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [dsl]);

  const lines = dsl.split('\n');

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-surface-700">
        <span className="text-xs font-semibold text-surface-400 uppercase tracking-wider">TC-DSL Dry Run</span>
        <button
          onClick={handleCopy}
          className={clsx(
            'text-xs px-2.5 py-1 rounded-md transition-all duration-150',
            copied
              ? 'bg-emerald-500/20 text-emerald-300'
              : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700/50',
          )}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>

      {/* Code */}
      <div className="flex-1 overflow-auto font-mono text-xs leading-relaxed">
        <div className="py-3 min-w-fit">
          {lines.map((line, i) => (
            <DslLine key={i} line={line} lineNumber={i + 1} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Syntax-highlighted DSL line */
function DslLine({ line, lineNumber }: { line: string; lineNumber: number }) {
  const highlighted = highlightDsl(line);
  const isGroupLine = /^\s*group\s+"/.test(line);
  return (
    <div
      className="flex px-4"
      style={isGroupLine ? {
        background: 'rgba(168, 85, 247, 0.08)',
        borderLeft: '2px solid rgba(168, 85, 247, 0.35)',
      } : undefined}
    >
      <span className="w-8 shrink-0 text-right pr-3 text-surface-600 select-none text-[10px] leading-relaxed">
        {lineNumber}
      </span>
      <span
        className="text-surface-200 whitespace-pre"
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </div>
  );
}

/** Simple DSL syntax highlighting */
function highlightDsl(line: string): string {
  // Strategy header
  if (/^strategy\s+"/.test(line.trim())) {
    return line.replace(
      /^(\s*)(strategy)(\s+"[^"]*":)/,
      `$1<span class="text-primary-400 font-semibold">$2</span><span class="text-amber-300">$3</span>`,
    );
  }

  // Keywords
  const keywords = ['equal:', 'weights:', 'else:', 'asset_class:', 'rebalance:', 'description:'];
  for (const kw of keywords) {
    if (line.trim().startsWith(kw) || line.trim() === kw) {
      return line.replace(kw, `<span class="text-sky-400">${kw}</span>`);
    }
  }

  // if keyword
  if (/^\s*if\s/.test(line)) {
    return line.replace(
      /^(\s*)(if)\s(.+)(:)$/,
      `$1<span class="text-sky-400 font-semibold">$2</span> <span class="text-surface-300">$3</span><span class="text-sky-400">$4</span>`,
    );
  }

  // filter keyword
  if (/^\s*filter\s/.test(line)) {
    return line.replace(
      /^(\s*)(filter)\s(.+)(:)$/,
      `$1<span class="text-amber-400 font-semibold">$2</span> <span class="text-surface-300">$3</span><span class="text-amber-400">$4</span>`,
    );
  }

  // inv_vol
  if (/^\s*inv_vol\(/.test(line)) {
    return line.replace(
      /^(\s*)(inv_vol\(\d+\))(:)$/,
      `$1<span class="text-purple-400">$2</span><span class="text-purple-400">$3</span>`,
    );
  }

  // group
  if (/^\s*group\s+"/.test(line)) {
    return line.replace(
      /^(\s*)(group)(\s+"[^"]*":)/,
      `$1<span class="text-purple-400">$2</span><span class="text-amber-300">$3</span>`,
    );
  }

  // Weight percentages in list items: - 60%: TICKER
  if (/^\s*-\s+\d+%:/.test(line)) {
    return line.replace(
      /^(\s*-\s+)(\d+%)(:)/,
      `$1<span class="text-emerald-400 font-semibold">$2</span><span class="text-surface-400">$3</span>`,
    );
  }

  // Tickers (uppercase words, possibly as list items)
  if (/^\s*-?\s*[A-Z]{1,6}(\s|$)/.test(line)) {
    return line.replace(
      /\b([A-Z]{1,6})\b/,
      `<span class="text-emerald-300">$1</span>`,
    );
  }

  return line;
}
